import { useEffect, useState } from "react";
import { api, apiError, imgUrl } from "../lib/api";
import { toast } from "sonner";
import { Upload, X, Image, Link2, CheckCircle2, Search, Folder, Clock, FileText, AlertTriangle, Download, History, Plus } from "lucide-react";

const STATUS_STYLE = {
  pending: "bg-zinc-100 text-zinc-700",
  approved: "bg-emerald-100 text-emerald-800",
  final: "bg-blue-100 text-blue-800",
  rejected: "bg-red-100 text-red-700",
};

const TYPE_FILTERS = ["all", "image", "document", "video", "3d_model"];

export default function DAM() {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState("all");
  const [tagSearch, setTagSearch] = useState("");
  const [showUpload, setShowUpload] = useState(false);
  const [form, setForm] = useState({ name: "", asset_type: "image", tags: "", file_url: "", collection_id: "", source_record_type: "", source_record_id: "" });
  const [saving, setSaving] = useState(false);
  const [collections, setCollections] = useState([]);
  const [collectionFilter, setCollectionFilter] = useState("all");
  const [selectedAsset, setSelectedAsset] = useState(null);

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Version history for detail panel
  const [assetVersions, setAssetVersions] = useState([]);
  const [newVersionUrl, setNewVersionUrl] = useState("");
  const [uploadingVersion, setUploadingVersion] = useState(false);

  const load = () => {
    api.get("/digital-assets")
      .then((r) => setAssets(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    api.get("/collections").then((r) => setCollections(r.data)).catch(() => {});
  }, []);

  const upload = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        asset_type: form.asset_type,
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
        file_url: form.file_url,
      };
      if (form.collection_id) payload.collection_id = form.collection_id;
      if (form.source_record_type && form.source_record_id) {
        payload.source_record_type = form.source_record_type;
        payload.source_record_id = form.source_record_id;
      }
      await api.post("/digital-assets", payload);
      toast.success("Asset created");
      setShowUpload(false);
      setForm({ name: "", asset_type: "image", tags: "", file_url: "", collection_id: "", source_record_type: "", source_record_id: "" });
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/digital-assets/${id}/status`, { status });
      toast.success(`Asset ${status}`);
      load();
      if (selectedAsset?.id === id) setSelectedAsset(null);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const generateShareLink = async (id) => {
    try {
      const r = await api.post(`/digital-assets/${id}/share`, { expiry_days: 7 });
      const shareUrl = `${window.location.origin}${r.data.url}`;
      await navigator.clipboard.writeText(shareUrl);
      toast.success(`Share link copied (expires ${r.data.expires_at ? new Date(r.data.expires_at).toLocaleDateString() : "in 7 days"})`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const downloadAsset = async (id) => {
    try {
      const r = await api.get(`/digital-assets/${id}/download`);
      if (r.data.file_url) {
        window.open(r.data.file_url, "_blank");
      }
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  // Load versions when an asset is selected
  useEffect(() => {
    if (selectedAsset?.id) {
      api.get(`/digital-assets/${selectedAsset.id}/versions`)
        .then((r) => setAssetVersions(Array.isArray(r.data) ? r.data : []))
        .catch(() => setAssetVersions([]));
    } else {
      setAssetVersions([]);
    }
    setNewVersionUrl("");
  }, [selectedAsset?.id]);

  const toggleSelect = (id, e) => {
    e.stopPropagation();
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const bulkDownload = () => {
    selectedIds.forEach((id) => {
      downloadAsset(id);
    });
  };

  const uploadNewVersion = async () => {
    if (!selectedAsset || !newVersionUrl) return;
    setUploadingVersion(true);
    try {
      await api.post(`/digital-assets/${selectedAsset.id}/versions`, { file_url: newVersionUrl });
      toast.success("New version uploaded");
      setNewVersionUrl("");
      load();
      // Refresh versions
      const r = await api.get(`/digital-assets/${selectedAsset.id}/versions`);
      setAssetVersions(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setUploadingVersion(false);
    }
  };

  const filtered = assets.filter((a) => {
    if (typeFilter !== "all" && a.asset_type !== typeFilter) return false;
    if (collectionFilter !== "all" && (a.collection_id || "") !== collectionFilter) return false;
    if (tagSearch) {
      const q = tagSearch.toLowerCase();
      const tags = Array.isArray(a.tags) ? a.tags.join(" ").toLowerCase() : (a.tags || "").toLowerCase();
      const name = (a.name || "").toLowerCase();
      if (!tags.includes(q) && !name.includes(q)) return false;
    }
    return true;
  });

  // Group by collection for folder view
  const collectionNames = {};
  collections.forEach((c) => { collectionNames[c.id] = c.name; });

  // Summary stats
  const totalCount = assets.length;
  const pendingCount = assets.filter((a) => a.status === "pending").length;
  const approvedCount = assets.filter((a) => a.status === "approved" || a.status === "final").length;
  const expiringCount = assets.filter((a) => a.rights_expiry && new Date(a.rights_expiry) < new Date(Date.now() + 30 * 86400000)).length;

  return (
    <div className="p-10 max-w-6xl mx-auto">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-zinc-400">PLM</p>
          <h1 className="font-heading text-3xl font-bold mt-2 tracking-tight">Digital Asset Management</h1>
          <p className="text-zinc-500 mt-2 text-sm">Upload, organize and share design assets with approval workflow.</p>
        </div>
        <button onClick={() => setShowUpload(true)}
          className="inline-flex items-center gap-2 bg-zinc-950 text-white px-5 py-2.5 rounded-md text-sm font-medium hover:bg-zinc-800 transition-colors">
          <Upload size={15} /> Upload asset
        </button>
      </div>

      {/* Summary Cards */}
      <div className="mt-6 grid grid-cols-4 gap-4">
        <div className="border border-zinc-200 bg-white rounded-lg p-4">
          <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">Total Assets</p>
          <p className="text-2xl font-bold mt-1">{totalCount}</p>
        </div>
        <div className="border border-zinc-200 bg-white rounded-lg p-4">
          <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">Pending Review</p>
          <p className="text-2xl font-bold mt-1 text-amber-600">{pendingCount}</p>
        </div>
        <div className="border border-zinc-200 bg-white rounded-lg p-4">
          <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">Approved / Final</p>
          <p className="text-2xl font-bold mt-1 text-emerald-600">{approvedCount}</p>
        </div>
        <div className="border border-zinc-200 bg-white rounded-lg p-4">
          <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">Rights Expiring</p>
          <p className="text-2xl font-bold mt-1 text-red-600">{expiringCount}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="mt-6 flex flex-wrap gap-3 items-center">
        <div className="flex gap-2">
          {TYPE_FILTERS.map((t) => (
            <button key={t} onClick={() => setTypeFilter(t)}
              className={`px-3 py-1.5 rounded-md text-sm capitalize ${typeFilter === t ? "bg-zinc-950 text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`}>
              {t === "all" ? "All" : t.replace("_", " ")}
            </button>
          ))}
        </div>
        {/* Collection / Folder Filter */}
        <select value={collectionFilter} onChange={(e) => setCollectionFilter(e.target.value)}
          className="border border-zinc-300 rounded-md px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-950">
          <option value="all">All collections</option>
          {collections.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          <option value="">Unassigned</option>
        </select>
        <div className="relative flex-1 max-w-xs">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input value={tagSearch} onChange={(e) => setTagSearch(e.target.value)} placeholder="Search by tags or name..."
            className="w-full pl-9 pr-3 py-2 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
        </div>
      </div>

      {loading ? (
        <div className="mt-10 text-sm text-zinc-400">Loading...</div>
      ) : filtered.length === 0 ? (
        <div className="mt-10 border-2 border-dashed border-zinc-200 rounded-lg p-16 text-center text-zinc-400">
          <Image size={26} className="mx-auto mb-3" />
          <p className="text-sm">No assets found.</p>
        </div>
      ) : (
        <div className="mt-6 flex gap-6">
          <div className="flex-1 grid grid-cols-2 md:grid-cols-3 gap-4">
            {filtered.map((a) => {
              const thumbSrc = a.thumbnail_url || (a.asset_type === "image" && a.file_url ? a.file_url : null);
              return (
              <div key={a.id} className={`relative border bg-white rounded-lg overflow-hidden hover:shadow-sm transition-shadow group cursor-pointer ${selectedAsset?.id === a.id ? "border-zinc-950 ring-2 ring-zinc-950" : "border-zinc-200"}`}
                onClick={() => setSelectedAsset(a)}>
                <div className="absolute top-2 left-2 z-10">
                  <input type="checkbox" checked={selectedIds.has(a.id)}
                    onChange={(e) => toggleSelect(a.id, e)}
                    className="w-4 h-4 accent-zinc-950 rounded cursor-pointer" />
                </div>
                {thumbSrc ? (
                  <img src={imgUrl(thumbSrc)} alt={a.name} className="w-full h-40 object-cover" />
                ) : (
                  <div className="w-full h-40 bg-zinc-100 flex items-center justify-center">
                    <Image size={24} className="text-zinc-300" />
                  </div>
                )}
                <div className="p-3">
                  <div className="flex items-start justify-between">
                    <p className="text-sm font-medium truncate">{a.name}</p>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium shrink-0 ml-1 ${STATUS_STYLE[a.status] || STATUS_STYLE.pending}`}>
                      {a.status}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-zinc-400 mt-0.5">
                    <span>{a.asset_type?.replace("_", " ")}</span>
                    {a.version > 1 && <span className="font-mono">v{a.version}</span>}
                  </div>
                  {a.collection_id && collectionNames[a.collection_id] && (
                    <div className="flex items-center gap-1 text-[10px] text-zinc-400 mt-1">
                      <Folder size={10} /> {collectionNames[a.collection_id]}
                    </div>
                  )}
                  {a.tags && <p className="text-[10px] text-zinc-400 mt-1">{Array.isArray(a.tags) ? a.tags.join(", ") : a.tags}</p>}
                  {/* Rights expiry warning */}
                  {a.rights_expiry && new Date(a.rights_expiry) < new Date(Date.now() + 30 * 86400000) && (
                    <div className="flex items-center gap-1 text-[10px] text-red-600 mt-1">
                      <AlertTriangle size={10} /> Rights expire {new Date(a.rights_expiry).toLocaleDateString()}
                    </div>
                  )}
                </div>
              </div>
              );
            })}
          </div>

          {/* Detail Panel */}
          {selectedAsset && (
            <div className="w-80 shrink-0 border border-zinc-200 bg-white rounded-lg p-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-heading font-semibold text-sm">{selectedAsset.name}</h3>
                <button onClick={() => setSelectedAsset(null)} className="p-1 hover:bg-zinc-100 rounded"><X size={14} /></button>
              </div>
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${STATUS_STYLE[selectedAsset.status] || STATUS_STYLE.pending}`}>
                {selectedAsset.status}
              </span>
              <div className="mt-4 space-y-3">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">Type</p>
                  <p className="text-sm">{selectedAsset.asset_type?.replace("_", " ")}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">Version</p>
                  <p className="text-sm font-mono">v{selectedAsset.version || 1}</p>
                </div>
                {selectedAsset.collection_id && collectionNames[selectedAsset.collection_id] && (
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">Collection</p>
                    <p className="text-sm flex items-center gap-1"><Folder size={12} /> {collectionNames[selectedAsset.collection_id]}</p>
                  </div>
                )}
                {selectedAsset.source_record_type && (
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">Linked To</p>
                    <p className="text-sm flex items-center gap-1"><FileText size={12} /> {selectedAsset.source_record_type} {selectedAsset.source_record_id?.slice(0, 8)}</p>
                  </div>
                )}
                {selectedAsset.rights_expiry && (
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">Rights Expiry</p>
                    <p className={`text-sm flex items-center gap-1 ${new Date(selectedAsset.rights_expiry) < new Date() ? "text-red-600" : ""}`}>
                      <Clock size={12} /> {new Date(selectedAsset.rights_expiry).toLocaleDateString()}
                    </p>
                  </div>
                )}
                {selectedAsset.metadata_json && Object.keys(selectedAsset.metadata_json).length > 0 && (
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">Metadata</p>
                    <div className="mt-1 space-y-0.5">
                      {Object.entries(selectedAsset.metadata_json).map(([k, v]) => (
                        <div key={k} className="text-xs"><span className="text-zinc-400">{k}:</span> <span className="font-medium">{String(v)}</span></div>
                      ))}
                    </div>
                  </div>
                )}
                {selectedAsset.created_at && (
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">Created</p>
                    <p className="text-sm text-zinc-500">{new Date(selectedAsset.created_at).toLocaleDateString()}</p>
                  </div>
                )}
              </div>

              {/* Version History */}
              <div className="mt-5 pt-4 border-t border-zinc-100">
                <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 flex items-center gap-1 mb-2">
                  <History size={10} /> Version History
                </p>
                {assetVersions.length > 0 ? (
                  <div className="space-y-1 mb-3">
                    {assetVersions.map((v, i) => (
                      <div key={v.id || i} className="flex items-center justify-between text-xs py-1.5 px-2 bg-zinc-50 rounded">
                        <span className="font-mono font-medium">v{v.version_number || i + 1}</span>
                        {v.created_at && (
                          <span className="text-zinc-400">{new Date(v.created_at).toLocaleDateString()}</span>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-zinc-400 mb-3">No previous versions.</p>
                )}
                <div className="space-y-2">
                  <input value={newVersionUrl} onChange={(e) => setNewVersionUrl(e.target.value)}
                    placeholder="New version file URL"
                    className="w-full border border-zinc-200 rounded-md px-2.5 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-zinc-950" />
                  <button onClick={uploadNewVersion} disabled={!newVersionUrl || uploadingVersion}
                    className="w-full text-xs text-zinc-700 hover:text-zinc-900 inline-flex items-center justify-center gap-1 py-1.5 bg-zinc-100 rounded-md disabled:opacity-40">
                    <Plus size={10} /> {uploadingVersion ? "Uploading..." : "Upload New Version"}
                  </button>
                </div>
              </div>

              <div className="mt-5 pt-4 border-t border-zinc-100 space-y-2">
                {selectedAsset.status === "pending" && (
                  <button onClick={() => updateStatus(selectedAsset.id, "approved")}
                    className="w-full text-xs text-emerald-700 hover:text-emerald-900 inline-flex items-center justify-center gap-1 py-2 bg-emerald-50 rounded-md">
                    <CheckCircle2 size={12} /> Approve
                  </button>
                )}
                {selectedAsset.status === "approved" && (
                  <button onClick={() => updateStatus(selectedAsset.id, "final")}
                    className="w-full text-xs text-blue-700 hover:text-blue-900 inline-flex items-center justify-center gap-1 py-2 bg-blue-50 rounded-md">
                    <CheckCircle2 size={12} /> Mark as Final
                  </button>
                )}
                <button onClick={() => downloadAsset(selectedAsset.id)}
                  className="w-full text-xs text-zinc-700 hover:text-zinc-900 inline-flex items-center justify-center gap-1 py-2 bg-zinc-100 rounded-md">
                  Download
                </button>
                <button onClick={() => generateShareLink(selectedAsset.id)}
                  className="w-full text-xs text-zinc-500 hover:text-zinc-800 inline-flex items-center justify-center gap-1 py-2 bg-zinc-50 rounded-md">
                  <Link2 size={12} /> Share (7-day link)
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Bulk Download Bar */}
      {selectedIds.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-zinc-950 text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-4">
          <span className="text-sm">{selectedIds.size} asset{selectedIds.size !== 1 ? "s" : ""} selected</span>
          <button onClick={bulkDownload}
            className="inline-flex items-center gap-2 bg-white text-zinc-950 px-4 py-1.5 rounded-md text-sm font-medium hover:bg-zinc-100 transition-colors">
            <Download size={14} /> Download {selectedIds.size} selected
          </button>
          <button onClick={() => setSelectedIds(new Set())}
            className="text-zinc-400 hover:text-white p-1">
            <X size={14} />
          </button>
        </div>
      )}

      {showUpload && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={() => setShowUpload(false)}>
          <div className="bg-white rounded-lg p-8 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading font-semibold text-lg">Upload asset</h2>
              <button onClick={() => setShowUpload(false)} className="p-1.5 hover:bg-zinc-100 rounded-md"><X size={16} /></button>
            </div>
            <div className="space-y-4">
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Asset name"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <select value={form.asset_type} onChange={(e) => setForm({ ...form, asset_type: e.target.value })}
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-950">
                <option value="image">Image</option>
                <option value="document">Document</option>
                <option value="video">Video</option>
                <option value="3d_model">3D Model</option>
              </select>
              <div>
                <label className="text-xs text-zinc-500 mb-1 block">Collection (folder)</label>
                <select value={form.collection_id} onChange={(e) => setForm({ ...form, collection_id: e.target.value })}
                  className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-950">
                  <option value="">No collection</option>
                  {collections.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="Tags (comma separated)"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <input value={form.file_url} onChange={(e) => setForm({ ...form, file_url: e.target.value })} placeholder="File URL (https://...)"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <div>
                <label className="text-xs text-zinc-500 mb-1 block">Link to record (optional)</label>
                <div className="grid grid-cols-2 gap-2">
                  <select value={form.source_record_type} onChange={(e) => setForm({ ...form, source_record_type: e.target.value })}
                    className="border border-zinc-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-zinc-950">
                    <option value="">Type...</option>
                    <option value="design">Design</option>
                    <option value="techpack">Techpack</option>
                    <option value="collection">Collection</option>
                  </select>
                  <input value={form.source_record_id} onChange={(e) => setForm({ ...form, source_record_id: e.target.value })} placeholder="Record ID"
                    className="border border-zinc-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-zinc-950" />
                </div>
              </div>
              <button onClick={upload} disabled={!form.name || !form.file_url || saving}
                className="w-full bg-zinc-950 text-white py-3 rounded-md font-medium hover:bg-zinc-800 transition-colors disabled:opacity-40">
                {saving ? "Uploading..." : "Upload asset"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
